import React, { useId } from 'react';
import './DigitransLoader.css';

// Tracé suivi par la voiture : roue gauche → châssis → roue droite → passagers
const ROUTE =
  'M230 317A55 55 0 1 1 230 207A55 55 0 1 1 230 317L325 275Q410 282 470 252Q495 240 510 234' +
  'L553 262A55 55 0 1 1 663 262A55 55 0 1 1 553 262L510 176C498 140 478 90 458 90' +
  'C442 90 435 120 438 185L396 202C388 140 375 90 355 90C335 90 322 130 328 208';

const W = 860;
const H = 360;

function Wheel({ cx, delay }) {
  const cy = 262;
  return (
    <g className="dtl-bob" style={{ animationDelay: delay }}>
      <circle cx={cx} cy={cy} r="55" fill="none" className="s-ink" strokeWidth="26" />
      <g className="dtl-spin">
        <circle cx={cx} cy={cy} r="63" fill="none" className="s-tread" strokeWidth="9" pathLength="100" strokeDasharray="1.2 2.8" />
        <path
          d={`M${cx} ${cy - 30}V${cy + 30}M${cx - 26} ${cy - 15}L${cx + 26} ${cy + 15}M${cx - 26} ${cy + 15}L${cx + 26} ${cy - 15}`}
          className="s-ink" strokeWidth="3.5" strokeLinecap="round" opacity=".28" fill="none"
        />
        <circle cx={cx} cy={cy} r="8" className="f-ink" />
        <circle cx={cx + 3} cy={cy - 3} r="2.5" className="f-gloss" opacity=".5" />
      </g>
    </g>
  );
}

function Car({ id }) {
  return (
    <g>
      <polygon points="30,-13 120,-40 120,-6 30,-7" fill={`url(#${id}-beam)`} className="o-beam" />
      <polygon points="30,7 120,6 120,40 30,13" fill={`url(#${id}-beam)`} className="o-beam" />
      <rect x="-30" y="-13" width="64" height="32" rx="11" className="f-underbody" />
      <rect x="-23" y="-19" width="14" height="7" rx="2.5" className="f-tyre" />
      <rect x="11" y="-19" width="14" height="7" rx="2.5" className="f-tyre" />
      <rect x="-23" y="12" width="14" height="7" rx="2.5" className="f-tyre" />
      <rect x="11" y="12" width="14" height="7" rx="2.5" className="f-tyre" />
      <rect x="-32" y="-15" width="64" height="30" rx="11" className="f-accent" />
      <rect x="-30" y="-15" width="60" height="9" rx="6" className="f-gloss" opacity=".2" />
      <rect x="-11" y="-12" width="17" height="24" rx="5" className="f-gloss" opacity=".14" />
      <rect x="6" y="-11.5" width="11" height="23" rx="4" className="f-glass" />
      <rect x="-23" y="-10" width="8" height="20" rx="3" className="f-glass" opacity=".9" />
      <rect x="27.5" y="-12" width="4" height="6" rx="1.5" className="f-headlight" />
      <rect x="27.5" y="6" width="4" height="6" rx="1.5" className="f-headlight" />
      <rect x="-32" y="-11" width="3" height="5" rx="1" className="f-taillight" />
      <rect x="-32" y="6" width="3" height="5" rx="1" className="f-taillight" />
    </g>
  );
}

/**
 * Loader animé digiTRANS. Couleurs lues dans les tokens (DigitransLoader.css) :
 * 'auto' suit le thème de la page ; 'light' / 'dark' forcent une palette.
 * @param {number} size   Hauteur en px (largeur = size × 2.39)
 * @param {'light'|'dark'|'auto'} theme
 * @param {number} speed  Multiplicateur de vitesse (1 = cycle de 5 s)
 * @param {string} label  Texte lu par les lecteurs d'écran
 * @param {boolean} drawn  Départ avec le logo déjà tracé (cycle avancé, DigitransLoader.css) :
 *   aucun cadre vide à la première image (en-tête d'une réponse qui commence, lot 1)
 */
export default function DigitransLoader({ size = 48, theme = 'auto', speed = 1, label = 'Chargement', drawn = false, className = '', style }) {
  const id = 'dtl' + String(useId()).replace(/[^a-zA-Z0-9_-]/g, '');
  const s = Math.max(0.1, Number(speed) || 1);
  return (
    <span
      role="status"
      aria-label={label}
      className={`dtl dtl--${theme} ${drawn ? 'dtl--drawn' : ''} ${className}`.replace(/\s+/g, ' ').trim()}
      style={{ height: size, width: (size * W) / H, '--dtl-cycle': `${5 / s}s`, '--dtl-speed': s, ...style }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} aria-hidden="true" focusable="false">
        <defs>
          {/* Masques de luminance : blanc = visible, noir = masqué (pas des couleurs de thème) */}
          <mask id={`${id}-reveal`} maskUnits="userSpaceOnUse" x="0" y="0" width={W} height={H}>
            <path className="dtl-reveal" d={ROUTE} pathLength="1" fill="none" stroke="white" strokeWidth="46" />
          </mask>
          <linearGradient id={`${id}-edge`} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="black" />
            <stop offset=".2" stopColor="white" />
            <stop offset=".8" stopColor="white" />
            <stop offset="1" stopColor="black" />
          </linearGradient>
          <mask id={`${id}-fade`} maskUnits="userSpaceOnUse" x="0" y="0" width={W} height={H}>
            <rect width={W} height={H} fill={`url(#${id}-edge)`} />
          </mask>
          <linearGradient id={`${id}-beam`} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" className="st-beam" stopOpacity=".95" />
            <stop offset="1" className="st-beam" stopOpacity="0" />
          </linearGradient>
          <filter id={`${id}-blur`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="7 0" />
          </filter>
          <filter id={`${id}-soft`} x="-20%" y="-200%" width="140%" height="500%">
            <feGaussianBlur stdDeviation="5" />
          </filter>
        </defs>

        <g className="dtl-fx" mask={`url(#${id}-fade)`}>
          <line className="dtl-road s-road" x1="0" x2={W} y1="340" y2="340" strokeWidth="7" strokeLinecap="round" strokeDasharray="60 46" />
          <g className="s-speed" strokeWidth="5" strokeLinecap="round">
            <line className="dtl-speed" x1="70" x2="200" y1="110" y2="110" />
            <line className="dtl-speed" x1="30" x2="160" y1="168" y2="168" style={{ animationDelay: '-.21s' }} />
            <line className="dtl-speed" x1="80" x2="180" y1="222" y2="222" style={{ animationDelay: '-.4s' }} />
            <line className="dtl-speed" x1="40" x2="150" y1="290" y2="290" style={{ animationDelay: '-.12s' }} />
          </g>
          <g className="f-dust">
            <circle className="dtl-dust" cx="168" cy="322" r="13" />
            <circle className="dtl-dust" cx="176" cy="326" r="10" style={{ animationDelay: '-.3s' }} />
            <circle className="dtl-dust" cx="160" cy="318" r="15" style={{ animationDelay: '-.6s' }} />
            <circle className="dtl-dust" cx="548" cy="324" r="9" style={{ animationDelay: '-.45s' }} />
          </g>
        </g>

        <g className="dtl-exit">
          <g className="dtl-fx">
            <ellipse className="dtl-shadow f-shadow" cx="420" cy="334" rx="255" ry="9" filter={`url(#${id}-soft)`} />
            <g className="dtl-flicker">
              <polygon points="512,226 860,168 860,334 512,242" fill={`url(#${id}-beam)`} className="o-beam" />
              <circle cx="512" cy="234" r="6" className="f-beam" />
            </g>
          </g>

          <g opacity=".2">
            <g className="dtl-fx">
              <use href={`#${id}-mark`} transform="translate(-26 0)" filter={`url(#${id}-blur)`} />
            </g>
          </g>

          <g id={`${id}-mark`} mask={`url(#${id}-reveal)`}>
            <Wheel cx={230} delay="0s" />
            <Wheel cx={608} delay="-.15s" />
            <g className="dtl-body s-ink" fill="none" strokeWidth="22" strokeLinecap="round">
              <path d="M325 275Q410 282 470 252Q495 240 508 232" />
              <circle cx="510" cy="234" r="13" className="f-ink" stroke="none" />
              <path d="M328 208C322 130 335 90 355 90C375 90 388 140 396 202" />
              <path d="M438 185C435 120 442 90 458 90C478 90 498 140 510 176" />
            </g>
          </g>

          <g className="dtl-body f-accent">
            <g className="dtl-pop">
              <g className="dtl-lag"><rect x="325" y="22" width="37" height="38" rx="2" /></g>
            </g>
            <g className="dtl-pop dtl-pop--late">
              <g className="dtl-lag dtl-lag--late"><rect x="440" y="35" width="25" height="25" rx="2" /></g>
            </g>
          </g>
        </g>

        <g className="dtl-car-fade">
          <g className="dtl-car" style={{ offsetPath: `path('${ROUTE}')` }}>
            <Car id={id} />
          </g>
        </g>
      </svg>
    </span>
  );
}
